import {
  AxiosError,
  clearImpersonationSession,
  exitImpersonation,
  getActiveRefreshToken,
  setStoredImpersonationSession,
} from '@repo/api';
import { useSnackbar, useUserDetailsQuery } from '@repo/lib';
import { Badge, Button } from '@ui/components';
import { LogOutIcon, UserCogIcon } from 'lucide-react';
import { useEffect } from 'react';

const TITLE_PREFIX = '[IMPERSONATING] ';

type EnvironmentInfo = { label: string; className: string };

const ENV_INFO: Record<
  'local' | 'dev' | 'stage' | 'prod' | 'unknown',
  EnvironmentInfo
> = {
  local: {
    label: 'LOCAL',
    className: 'border-slate-600/70 bg-slate-100/80 text-slate-900',
  },
  dev: {
    label: 'DEV',
    className: 'border-sky-700/70 bg-sky-100/80 text-sky-900',
  },
  stage: {
    label: 'STAGE',
    className: 'border-violet-700/70 bg-violet-100/80 text-violet-900',
  },
  prod: {
    label: 'PROD',
    className: 'border-red-700/70 bg-red-100/80 text-red-900',
  },
  unknown: {
    label: 'UNKNOWN',
    className: 'border-slate-600/70 bg-slate-100/80 text-slate-900',
  },
};

const detectEnvironment = (): EnvironmentInfo => {
  const host = (import.meta.env.VITE_BACKEND_HOST ?? '').toLowerCase();

  if (
    host.includes('localhost') ||
    host.includes('127.0.0.1') ||
    host.includes('0.0.0.0')
  ) {
    return ENV_INFO.local;
  }
  if (host.includes('awstest')) {
    return ENV_INFO.dev;
  }
  if (host.includes('stage.')) {
    return ENV_INFO.stage;
  }
  if (host.includes('portal.acg.group')) {
    return ENV_INFO.prod;
  }
  return ENV_INFO.unknown;
};

const ENVIRONMENT = detectEnvironment();

const extractDetail = (
  error: AxiosError<{ detail?: string }>,
): string | undefined => error.response?.data?.detail;

export const ImpersonationBanner = () => {
  const { errorSnackbar } = useSnackbar();
  const { user } = useUserDetailsQuery();
  const impersonation = user?.impersonation;
  const isActive = impersonation?.active === true;

  useEffect(() => {
    if (!user) {
      return;
    }

    setStoredImpersonationSession(user.impersonation);
  }, [user]);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    const original = document.title;
    if (!original.startsWith(TITLE_PREFIX)) {
      document.title = `${TITLE_PREFIX}${original}`;
    }

    return () => {
      if (document.title.startsWith(TITLE_PREFIX)) {
        document.title = document.title.slice(TITLE_PREFIX.length);
      }
    };
  }, [isActive]);

  if (!isActive || !impersonation || impersonation.active !== true) {
    return null;
  }

  const handleExit = async () => {
    const refresh = getActiveRefreshToken();
    const fallbackUrl = impersonation.returnUrl;

    try {
      const result = await exitImpersonation(refresh ?? '');
      // Clear only the tab-scoped impersonation tokens. localStorage (the
      // impersonator's own session, possibly active in sibling tabs) is left
      // untouched.
      clearImpersonationSession();
      window.location.href = result.returnUrl || fallbackUrl;
    } catch (error) {
      const axiosError = error as AxiosError<{ detail?: string }>;
      errorSnackbar(
        extractDetail(axiosError) || 'Failed to exit impersonation',
      );
      clearImpersonationSession();
      window.location.href = fallbackUrl;
    }
  };

  return (
    <div
      className="sticky top-0 z-[10001] flex w-full items-center justify-between gap-4 border-y-2 border-primary/45 bg-[linear-gradient(90deg,rgba(200,220,255,0.92),rgba(225,241,255,0.9))] px-4 py-2.5 text-sm text-primary shadow-sm backdrop-blur-sm"
      role="status"
    >
      <div className="flex items-center gap-2">
        <UserCogIcon className="size-4 shrink-0" />
        <span className="font-semibold">
          You are impersonating user:{' '}
          <strong>{impersonation.targetFullName}</strong> (
          {impersonation.targetUsername})
        </span>
        <Badge
          className="border-2 border-primary/35 bg-background/80 px-2 py-0.5 text-[10px] uppercase tracking-[0.08em] text-primary"
          variant="outline"
        >
          Read-only
        </Badge>
        <Badge
          className={`border-2 px-2 py-0.5 text-[10px] uppercase tracking-[0.08em] ${ENVIRONMENT.className}`}
          variant="outline"
        >
          {ENVIRONMENT.label}
        </Badge>
      </div>
      <Button
        className="border-2 border-primary/55 bg-background/80 text-primary shadow-sm hover:bg-primary hover:text-primary-foreground"
        size="sm"
        type="button"
        variant="outline"
        onClick={handleExit}
      >
        <LogOutIcon className="mr-2 size-4" />
        Exit Impersonation
      </Button>
    </div>
  );
};
